// src/firebase/auth.js (or wherever this file lives)
// Lazy load Firebase - imports will be loaded when needed
import { auth, ensureFirebaseInitialized } from "./config";

// Check if auth is available
const isAuthAvailable = () => {
  return auth !== null;
};

// Lazy load Google Auth Provider
let googleProvider = null;
const getGoogleProvider = async () => {
  if (googleProvider) return googleProvider;
  
  // Ensure Firebase is initialized
  await ensureFirebaseInitialized();
  
  // Dynamically import firebase/compat/app to get auth provider
  const firebaseApp = await import("firebase/compat/app");
  const firebase = firebaseApp.default;
  
  googleProvider = new firebase.auth.GoogleAuthProvider();
  return googleProvider;
};

/* ---------------- Email/Password ---------------- */

export const signUpWithEmail = async (email, password, displayName) => {
  if (!isAuthAvailable()) {
    throw new Error('Firebase Auth is not available');
  }
  const userCredential = await auth.createUserWithEmailAndPassword(email, password);
  if (displayName) await userCredential.user.updateProfile({ displayName });
  return userCredential.user;
};

export const signInWithEmail = async (email, password) => {
  if (!isAuthAvailable()) {
    throw new Error('Firebase Auth is not available');
  }
  const { user } = await auth.signInWithEmailAndPassword(email, password);
  return user;
};

/* ---------------- Google ---------------- */

// Use this when your button says: "Sign in with Google"
export const signInWithGoogle = async () => {
  const provider = await getGoogleProvider();
  const result = await auth.signInWithPopup(provider);
  // result.user is returned whether it's new or existing
  return result.user;
};

// Use this when your button says: "Sign up with Google"
export const signUpWithGoogle = async () => {
  const provider = await getGoogleProvider();
  const result = await auth.signInWithPopup(provider);
  const info = result.additionalUserInfo;

  // If not a new user, you can block sign-up and ask them to use Sign in
  if (!info || !info.isNewUser) {
    // Optional: sign out to avoid leaving them signed in from the popup flow
    await auth.signOut();
    const err = new Error("This Google account already exists. Please sign in instead.");
    err.code = "auth/account-exists";
    throw err;
  }
  return result.user;
};

/* ---------------- Misc ---------------- */

export const signOutUser = () => auth.signOut();

export const resetPassword = (email) => auth.sendPasswordResetEmail(email);

export const onAuthStateChange = (callback) => {
  // Track unsubscribe function
  let unsubscribe = null;
  let isUnsubscribed = false;
  
  // Try to set up listener immediately if auth is available
  if (isAuthAvailable()) {
    unsubscribe = auth.onAuthStateChanged(callback);
    return () => {
      if (unsubscribe && !isUnsubscribed) {
        isUnsubscribed = true;
        unsubscribe();
      }
    };
  }
  
  // If auth is not available, wait for initialization
  ensureFirebaseInitialized()
    .then(() => {
      // Wait a bit more for auth to be ready
      let retries = 0;
      const maxRetries = 20; // 4 seconds max wait
      const checkAuth = () => {
        if (isAuthAvailable() && !isUnsubscribed) {
          unsubscribe = auth.onAuthStateChanged(callback);
        } else if (retries < maxRetries && !isUnsubscribed) {
          retries++;
          setTimeout(checkAuth, 200);
        } else if (!isUnsubscribed) {
          console.warn('Firebase Auth is not available, using fallback');
          callback(null);
        }
      };
      checkAuth();
    })
    .catch((error) => {
      console.error('Error initializing Firebase for auth:', error);
      if (!isUnsubscribed) {
        callback(null);
      }
    });
  
  // Return unsubscribe function that works even if listener isn't set up yet
  return () => {
    isUnsubscribed = true;
    if (unsubscribe) {
      unsubscribe();
    }
  };
};
